/**
 * Microphone Permission Modals
 * Vanilla JS modal system for permission handling
 * Provides pre-prompt, blocked permission, and no microphone modals
 */

import { getDeviceInfo, getPermissionInstructions, openSettings, checkPermissionState } from './MicPermissionUtils.js';

/**
 * Get the path to a visual guide SVG based on platform and browser
 */
function getVisualGuidePath(deviceInfo) {
  const { platform, browser } = deviceInfo;
  
  // Try to find the agent-widget.js script to determine base path
  const scripts = document.getElementsByTagName('script');
  let basePath = '';
  
  for (let script of scripts) {
    const src = script.src;
    if (src && (src.includes('agent-widget.js') || src.includes('ttp-agent-sdk') || src.includes('ttp-sdk-front'))) {
      try {
        // Extract base path from script URL
        const url = new URL(src);
        basePath = src.substring(0, src.lastIndexOf('/'));
        break;
      } catch (e) {
        // If URL parsing fails, try to extract path manually
        const basePathMatch = src.match(/^(.*\/)[^\/]+$/);
        if (basePathMatch) {
          basePath = basePathMatch[1];
          break;
        }
      }
    }
  }
  
  // Determine which SVG to use
  let svgName = '';
  if (platform === 'ios') {
    svgName = 'ios-settings.svg';
  } else if (platform === 'android') {
    if (browser === 'chrome') {
      svgName = 'android-chrome.svg';
    } else {
      svgName = 'android-chrome.svg'; // Default to chrome for android
    }
  } else {
    // Desktop
    if (browser === 'chrome') {
      svgName = 'desktop-chrome.svg';
    } else if (browser === 'safari') {
      svgName = 'desktop-safari.svg';
    } else if (browser === 'firefox') {
      svgName = 'desktop-firefox.svg';
    } else if (browser === 'edge') {
      svgName = 'desktop-edge.svg';
    } else {
      svgName = 'desktop-chrome.svg'; // Default
    }
  }
  
  // If basePath is empty or relative, try fallbacks
  if (!basePath || basePath === '' || basePath.startsWith('/')) {
    // First, check if script was loaded from CDN
    for (let script of scripts) {
      const src = script.src;
      if (src && (src.includes('cdn.talktopc.com') || src.includes('talktopc.com'))) {
        // Extract CDN path
        if (src.includes('/ttp-agent-sdk/') || src.includes('/ttp-sdk-front/')) {
          const cdnMatch = src.match(/(https?:\/\/[^\/]+(?:\/[^\/]+)*)/);
          if (cdnMatch) {
            basePath = cdnMatch[1].replace(/\/[^\/]+\.js$/, '');
            break;
          }
        }
        // If script is from CDN but path extraction failed, use default CDN path
        if (!basePath && (src.includes('cdn.talktopc.com') || src.includes('talktopc.com'))) {
          basePath = 'https://cdn.talktopc.com/ttp-agent-sdk';
          break;
        }
      }
    }
    
    // If still empty, check current page hostname
    if (!basePath || basePath === '') {
      if (window.location.hostname.includes('cdn.talktopc.com') || 
          window.location.hostname.includes('talktopc.com')) {
        basePath = 'https://cdn.talktopc.com/ttp-agent-sdk';
      } else {
        // Fallback: use current page's base path
        const pathParts = window.location.pathname.split('/');
        pathParts.pop(); // Remove last part (filename)
        basePath = window.location.origin + pathParts.join('/');
        // If still empty, use root
        if (!basePath || basePath === window.location.origin) {
          basePath = window.location.origin;
        }
      }
    }
  }
  
  // Ensure basePath doesn't end with /
  basePath = basePath.replace(/\/$/, '');
  
  // Return path - will be in dist/shared/mic-permission-assets/ after build
  const imagePath = `${basePath}/shared/mic-permission-assets/${svgName}`;
  
  // Debug logging (can be removed in production)
  if (typeof console !== 'undefined' && console.log) {
    console.log('[MicPermission] Visual guide path:', imagePath, 'Base path:', basePath, 'Platform:', platform, 'Browser:', browser);
  }
  
  return imagePath;
}

/**
 * Base modal class for creating permission-related modals
 */
class MicPermissionModal {
  constructor() {
    this.overlay = null;
    this.modal = null;
  }

  /**
   * Create modal overlay
   */
  createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'mic-permission-overlay';
    overlay.setAttribute('dir', 'ltr');
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 100001;
      padding: 20px;
      animation: mic-fadeIn 0.2s ease-out;
    `;
    
    // Add fade-in animation if not already added
    if (!document.getElementById('mic-permission-styles')) {
      this.injectStyles();
    }
    
    return overlay;
  }

  /**
   * Inject CSS styles for modals
   */
  injectStyles() {
    if (document.getElementById('mic-permission-styles')) {
      return; // Already injected
    }

    const style = document.createElement('style');
    style.id = 'mic-permission-styles';
    style.textContent = `
      @keyframes mic-fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes mic-slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Disclaimer card only: no opacity tween (avoids scrim looking like it “dims” mid-gesture with backdrop-filter) */
      @keyframes mic-slideUpSolid {
        from { transform: translateY(20px); }
        to { transform: translateY(0); }
      }
      
      @keyframes mic-bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      
      /* direction ltr: host page may be RTL (e.g. Hebrew); keep overlay + modal English copy readable */
      .mic-permission-overlay {
        position: fixed;
        inset: 0;
        direction: ltr;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100001;
        padding: 20px;
        animation: mic-fadeIn 0.2s ease-out;
      }
      
      .mic-permission-modal {
        direction: ltr;
        unicode-bidi: isolate;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        max-width: 480px;
        width: 100%;
        padding: 32px;
        position: relative;
        animation: mic-slideUp 0.3s ease-out;
        max-height: 90vh;
        overflow-y: auto;
      }
      
      .mic-permission-modal__close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        color: #6b7280;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }
      
      .mic-permission-modal__close:hover {
        background: #e5e7eb;
      }
      
      .mic-permission-modal__icon {
        font-size: 48px;
        text-align: center;
        margin-bottom: 16px;
      }
      
      .mic-permission-modal__title {
        font-size: 24px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 12px;
        line-height: 1.3;
        text-align: center;
      }
      
      .mic-permission-modal__description {
        color: #6b7280;
        font-size: 16px;
        line-height: 1.6;
        margin: 0 0 20px;
        text-align: center;
      }
      
      .mic-permission-modal__highlight {
        display: flex;
        align-items: center;
        gap: 12px;
        background: linear-gradient(135deg, #dbeafe, #ede9fe);
        padding: 16px 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid #bfdbfe;
      }
      
      .mic-permission-modal__highlight-icon {
        font-size: 24px;
        animation: mic-bounce 1s infinite;
      }
      
      .mic-permission-modal__status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: #fef2f2;
        color: #ef4444;
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 20px;
        font-weight: 500;
      }
      
      .mic-permission-modal__note {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        background: #fffbeb;
        color: #92400e;
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 20px;
        font-size: 14px;
        line-height: 1.5;
        border: 1px solid #fde68a;
      }
      
      .mic-permission-modal__steps {
        margin-bottom: 20px;
      }
      
      .mic-permission-modal__steps h3 {
        font-size: 14px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: 0 0 12px;
      }
      
      .mic-permission-modal__steps ol {
        margin: 0;
        padding-left: 24px;
        counter-reset: step-counter;
        list-style: none;
      }
      
      .mic-permission-modal__steps li {
        position: relative;
        padding: 12px 0 12px 16px;
        border-left: 2px solid #e5e7eb;
        color: #111827;
        font-size: 15px;
        line-height: 1.5;
        counter-increment: step-counter;
      }
      
      .mic-permission-modal__steps li::before {
        content: counter(step-counter);
        position: absolute;
        left: -14px;
        top: 10px;
        width: 24px;
        height: 24px;
        background: #4f46e5;
        color: white;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .mic-permission-modal__steps li:last-child {
        border-left-color: transparent;
      }
      
      .mic-permission-modal__actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 24px;
      }
      
      .mic-permission-modal__btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
        width: 100%;
      }
      
      .mic-permission-modal__btn--primary {
        background: #4f46e5;
        color: white;
      }
      
      .mic-permission-modal__btn--primary:hover {
        background: #4338ca;
        transform: translateY(-1px);
      }
      
      .mic-permission-modal__btn--secondary {
        background: #e5e7eb;
        color: #111827;
      }
      
      .mic-permission-modal__btn--secondary:hover {
        background: #d1d5db;
      }
      
      .mic-permission-modal__footer {
        text-align: center;
        font-size: 13px;
        color: #6b7280;
        margin-top: 20px;
        margin-bottom: 0;
      }
      
      .mic-permission-modal__visual-guide {
        width: 100%;
        max-width: 380px;
        height: auto;
        margin: 20px auto;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        display: block;
      }
      
      .mic-permission-modal__visual-guide-container {
        text-align: center;
        margin: 20px 0;
      }
      
      @media (max-width: 480px) {
        .mic-permission-modal {
          padding: 24px 20px;
          margin: 10px;
          max-height: 85vh;
        }
        
        .mic-permission-modal__title {
          font-size: 20px;
        }
        
        .mic-permission-modal__icon {
          font-size: 40px;
        }
        
        .mic-permission-modal__visual-guide {
          max-width: 100%;
        }
      }
      
      /* Mobile pre-prompt: compact, action-first layout - buttons always visible above safe area */
      .mic-permission-modal--mobile {
        max-height: none;
        padding: 20px 20px 24px;
        padding-bottom: max(24px, env(safe-area-inset-bottom, 24px));
        margin: 12px;
        align-self: flex-end;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__icon {
        font-size: 36px;
        margin-bottom: 12px;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__title {
        font-size: 18px;
        margin-bottom: 8px;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__description {
        font-size: 14px;
        margin-bottom: 12px;
        line-height: 1.5;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__highlight {
        padding: 12px 16px;
        margin-bottom: 12px;
        font-size: 14px;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__note {
        padding: 10px 14px;
        margin-bottom: 16px;
        font-size: 13px;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__actions {
        margin-top: 16px;
        gap: 10px;
      }
      
      .mic-permission-modal--mobile .mic-permission-modal__btn {
        padding: 16px 24px;
        font-size: 16px;
        min-height: 48px;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Close modal
   */
  close() {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
    this.modal = null;
  }

  /**
   * Show modal
   */
  show() {
    if (this.overlay && this.modal) {
      document.body.appendChild(this.overlay);
    }
  }
}

/**
 * Detect if we're on a mobile device (for layout decisions)
 */
function isMobileDevice() {
  const deviceInfo = getDeviceInfo();
  return deviceInfo.isIOS || deviceInfo.isAndroid;
}

/**
 * Pre-prompt modal - shown before requesting browser permission
 * Uses a transparent overlay (no blur) so the call view is visible behind it
 */
export function showPrePromptModal(onAccept, onCancel) {
  const modal = new MicPermissionModal();
  const overlay = modal.createOverlay();
  const deviceInfo = getDeviceInfo();
  const isMobile = isMobileDevice();
  
  // Remove blur and darken so the call view is visible behind
  overlay.style.background = 'rgba(0, 0, 0, 0.3)';
  overlay.style.backdropFilter = 'none';
  overlay.style.webkitBackdropFilter = 'none';
  
  const modalDiv = document.createElement('div');
  modalDiv.className = 'mic-permission-modal' + (isMobile ? ' mic-permission-modal--mobile' : '');
  
  // Mobile: simpler, action-focused copy. Desktop: full explanation.
  const title = isMobile ? 'Allow Microphone' : 'Enable Your Microphone';
  const description = isMobile
    ? 'Tap Continue, then tap Allow when your browser asks.'
    : 'To start a voice call with our agent, we need access to your microphone.';
  const highlightText = isMobile
    ? 'Tap <strong>"Allow"</strong> on the popup'
    : 'Please click <strong>"Allow"</strong> on the next popup';
  
  modalDiv.innerHTML = `
    <div class="mic-permission-modal__icon">🎤</div>
    <h2 class="mic-permission-modal__title">${title}</h2>
    <p class="mic-permission-modal__description">${description}</p>
    <div class="mic-permission-modal__highlight">
      <span class="mic-permission-modal__highlight-icon">👆</span>
      <span>${highlightText}</span>
    </div>
    ${deviceInfo.isIOS ? `
      <div class="mic-permission-modal__note">
        <span>ℹ️</span>
        <span>${isMobile ? 'If blocked, enable in Settings → Safari/App → Microphone' : 'On iPhone/iPad, you may need to allow microphone access in your device Settings.'}</span>
      </div>
    ` : ''}
    <div class="mic-permission-modal__actions">
      <button class="mic-permission-modal__btn mic-permission-modal__btn--primary" data-action="accept">
        Continue
      </button>
      <button class="mic-permission-modal__btn mic-permission-modal__btn--secondary" data-action="cancel">
        Not Now
      </button>
    </div>
  `;
  
  // On mobile: position modal at bottom so buttons are in thumb zone, above safe area
  if (isMobile) {
    overlay.style.alignItems = 'flex-end';
    overlay.style.paddingBottom = 'max(20px, env(safe-area-inset-bottom, 20px))';
    overlay.style.paddingTop = '20px';
  }
  
  overlay.appendChild(modalDiv);
  modal.overlay = overlay;
  modal.modal = modalDiv;
  
  modalDiv.querySelector('[data-action="accept"]').addEventListener('click', () => {
    modal.close();
    onAccept();
  });
  
  modalDiv.querySelector('[data-action="cancel"]').addEventListener('click', () => {
    modal.close();
    onCancel();
  });
  
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      modal.close();
      onCancel();
    }
  });
  
  modal.show();
  return modal;
}

/**
 * Show address bar hint - animated arrow pointing to lock/info icon
 */
function showAddressBarHint() {
  // Remove existing hint if any
  const existingHint = document.getElementById('mic-address-bar-hint');
  if (existingHint) {
    existingHint.remove();
  }
  
  // Get the address bar hint image path
  const deviceInfo = getDeviceInfo();
  const scripts = document.getElementsByTagName('script');
  let basePath = '';
  
  for (let script of scripts) {
    const src = script.src;
    if (src && (src.includes('agent-widget.js') || src.includes('ttp-agent-sdk') || src.includes('ttp-sdk-front'))) {
      try {
        const url = new URL(src);
        basePath = src.substring(0, src.lastIndexOf('/'));
        break;
      } catch (e) {
        const basePathMatch = src.match(/^(.*\/)[^\/]+$/);
        if (basePathMatch) {
          basePath = basePathMatch[1];
          break;
        }
      }
    }
  }
  
  // If basePath is empty or relative, try fallbacks
  if (!basePath || basePath === '' || basePath.startsWith('/')) {
    // First, check if script was loaded from CDN
    for (let script of scripts) {
      const src = script.src;
      if (src && (src.includes('cdn.talktopc.com') || src.includes('talktopc.com'))) {
        // Extract CDN path
        if (src.includes('/ttp-agent-sdk/') || src.includes('/ttp-sdk-front/')) {
          const cdnMatch = src.match(/(https?:\/\/[^\/]+(?:\/[^\/]+)*)/);
          if (cdnMatch) {
            basePath = cdnMatch[1].replace(/\/[^\/]+\.js$/, '');
            break;
          }
        }
        // If script is from CDN but path extraction failed, use default CDN path
        if (!basePath && (src.includes('cdn.talktopc.com') || src.includes('talktopc.com'))) {
          basePath = 'https://cdn.talktopc.com/ttp-agent-sdk';
          break;
        }
      }
    }
    
    // If still empty, check current page hostname
    if (!basePath || basePath === '') {
      if (window.location.hostname.includes('cdn.talktopc.com') || 
          window.location.hostname.includes('talktopc.com')) {
        basePath = 'https://cdn.talktopc.com/ttp-agent-sdk';
      } else {
        // Fallback: use current page's base path
        const pathParts = window.location.pathname.split('/');
        pathParts.pop(); // Remove last part (filename)
        basePath = window.location.origin + pathParts.join('/');
        // If still empty, use root
        if (!basePath || basePath === window.location.origin) {
          basePath = window.location.origin;
        }
      }
    }
  }
  
  // Ensure basePath doesn't end with /
  basePath = basePath.replace(/\/$/, '');
  
  const addressBarHintPath = `${basePath}/shared/mic-permission-assets/address-bar-hint.svg`;
  
  const hint = document.createElement('div');
  hint.id = 'mic-address-bar-hint';
  hint.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10001;
    padding: 10px 20px;
    pointer-events: none;
    animation: mic-fadeIn 0.3s ease-out;
    display: flex;
    justify-content: center;
  `;
  
  const content = document.createElement('div');
  content.style.cssText = `
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f59e0b;
    color: white;
    padding: 12px 20px;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: all;
    animation: mic-slideDown 0.3s ease-out;
    max-width: 540px;
  `;
  
  // Add the address bar hint image
  const imageContainer = document.createElement('div');
  imageContainer.style.cssText = `
    flex-shrink: 0;
  `;
  const img = document.createElement('img');
  img.src = addressBarHintPath;
  img.alt = 'Address bar hint';
  img.style.cssText = `
    width: auto;
    height: 80px;
    display: block;
  `;
  img.onerror = () => {
    // Hide image if it fails to load, but keep the text
    imageContainer.style.display = 'none';
  };
  imageContainer.appendChild(img);
  
  const textContainer = document.createElement('div');
  textContainer.style.cssText = `
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  `;
  
  const text = document.createElement('div');
  text.style.cssText = `
    font-size: 15px;
    font-weight: 500;
  `;
  text.innerHTML = 'Click the <strong>lock icon 🔒</strong>, <strong>information icon ℹ️</strong>, or <strong>settings icon ⋮</strong> in the address bar to enable microphone';
  
  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.2s;
    flex-shrink: 0;
  `;
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => {
    hint.remove();
  });
  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.background = 'rgba(255, 255, 255, 0.3)';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.background = 'rgba(255, 255, 255, 0.2)';
  });
  
  // Add animations if not already added
  if (!document.getElementById('mic-address-bar-hint-styles')) {
    const style = document.createElement('style');
    style.id = 'mic-address-bar-hint-styles';
    style.textContent = `
      @keyframes mic-slideDown {
        from {
          opacity: 0;
          transform: translateY(-100%);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes mic-pointUp {
        0%, 100% {
          transform: translate(0, 0);
        }
        50% {
          transform: translate(-5px, -5px);
        }
      }
    `;
    document.head.appendChild(style);
  }
  
  textContainer.appendChild(text);
  content.appendChild(imageContainer);
  content.appendChild(textContainer);
  content.appendChild(closeBtn);
  hint.appendChild(content);
  document.body.appendChild(hint);
  
  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    if (hint.parentNode) {
      hint.style.opacity = '0';
      hint.style.transition = 'opacity 0.3s ease-out';
      setTimeout(() => {
        if (hint.parentNode) {
          hint.remove();
        }
      }, 300);
    }
  }, 10000);
}

/**
 * Blocked permission modal - shown when user has denied mic access
 */
export function showBlockedModal(onClose, onRefresh) {
  const modal = new MicPermissionModal();
  const overlay = modal.createOverlay();
  const deviceInfo = getDeviceInfo();
  const instructions = getPermissionInstructions(deviceInfo);
  
  const modalDiv = document.createElement('div');
  modalDiv.className = 'mic-permission-modal';
  
  let altStepsHTML = '';
  if (instructions.altSteps && instructions.altSteps.length > 0) {
    altStepsHTML = `
      <div style="margin-top: 20px; border-top: 1px solid #e5e7eb; padding-top: 16px;">
        <button class="mic-permission-modal__alt-toggle" style="background: none; border: none; color: #4f46e5; font-size: 14px; font-weight: 500; cursor: pointer; padding: 0; margin-bottom: 12px;">
          ▼ ${instructions.altTitle || 'Alternative method'}
        </button>
        <ol style="margin: 0; padding-left: 20px; display: none;">
          ${instructions.altSteps.map(step => `<li style="padding: 8px 0; font-size: 14px; color: #6b7280;">${step}</li>`).join('')}
        </ol>
      </div>
    `;
  }
  
  // Get visual guide image path
  const visualGuidePath = getVisualGuidePath(deviceInfo);
  
  modalDiv.innerHTML = `
    <button class="mic-permission-modal__close" data-action="close">✕</button>
    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
      <span style="font-size: 32px;">${instructions.icon}</span>
      <h2 class="mic-permission-modal__title" style="text-align: left; margin: 0;">${instructions.title}</h2>
    </div>
    <div class="mic-permission-modal__status">
      <span>🚫</span>
      <span>Microphone access is currently blocked</span>
    </div>
    ${instructions.note ? `
      <div class="mic-permission-modal__note">
        <span>ℹ️</span>
        <span>${instructions.note}</span>
      </div>
    ` : ''}
    <div class="mic-permission-modal__visual-guide-container">
      <img src="${visualGuidePath}" alt="Visual guide for enabling microphone" class="mic-permission-modal__visual-guide" 
           onerror="console.error('[MicPermission] Failed to load image:', this.src); this.style.display='none';" 
           onload="console.log('[MicPermission] Image loaded successfully:', this.src);" />
    </div>
    <div class="mic-permission-modal__steps">
      <h3>Follow these steps:</h3>
      <ol>
        ${instructions.steps.map(step => `<li>${step}</li>`).join('')}
      </ol>
    </div>
    ${altStepsHTML}
    <div class="mic-permission-modal__actions">
      ${instructions.canOpenSettings ? `
        <button class="mic-permission-modal__btn mic-permission-modal__btn--primary" data-action="open-settings">
          ${instructions.settingsButtonText || 'Open Settings'}
        </button>
      ` : ''}
      ${instructions.showAddressBarHint ? `
        <button class="mic-permission-modal__btn mic-permission-modal__btn--primary" data-action="show-hint">
          Show Me Where
        </button>
      ` : ''}
      <button class="mic-permission-modal__btn mic-permission-modal__btn--secondary" data-action="refresh">
        🔄 Refresh Page
      </button>
    </div>
    <p class="mic-permission-modal__footer">
      After enabling microphone access, return here and click "Refresh Page"
    </p>
  `;
  
  overlay.appendChild(modalDiv);
  modal.overlay = overlay;
  modal.modal = modalDiv;
  
  // Event handlers
  modalDiv.querySelector('[data-action="close"]')?.addEventListener('click', () => {
    modal.close();
    onClose();
  });
  
  modalDiv.querySelector('[data-action="open-settings"]')?.addEventListener('click', () => {
    openSettings(deviceInfo);
  });
  
  modalDiv.querySelector('[data-action="refresh"]')?.addEventListener('click', () => {
    window.location.reload();
  });
  
  // Handle "Show Me Where" button - show address bar hint
  const showHintBtn = modalDiv.querySelector('[data-action="show-hint"]');
  if (showHintBtn) {
    showHintBtn.addEventListener('click', () => {
      // Close the modal
      modal.close();
      onClose();
      
      // Show address bar hint
      showAddressBarHint();
    });
  }
  
  // Handle alternative steps toggle
  const altToggle = modalDiv.querySelector('.mic-permission-modal__alt-toggle');
  if (altToggle && instructions.altSteps) {
    const altStepsList = altToggle.nextElementSibling;
    if (altStepsList) {
      altStepsList.style.display = 'none'; // Start hidden
      altToggle.addEventListener('click', () => {
        if (altStepsList.style.display === 'none') {
          altStepsList.style.display = 'block';
          altToggle.innerHTML = `▼ ${instructions.altTitle || 'Alternative method'}`;
        } else {
          altStepsList.style.display = 'none';
          altToggle.innerHTML = `▶ ${instructions.altTitle || 'Alternative method'}`;
        }
      });
    }
  }
  
  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      modal.close();
      onClose();
    }
  });
  
  modal.show();
  return modal;
}

/**
 * No microphone modal - shown when no mic device is found
 */
export function showNoMicrophoneModal(onClose) {
  const modal = new MicPermissionModal();
  const overlay = modal.createOverlay();
  
  const modalDiv = document.createElement('div');
  modalDiv.className = 'mic-permission-modal';
  
  modalDiv.innerHTML = `
    <div class="mic-permission-modal__icon">🎤❌</div>
    <h2 class="mic-permission-modal__title">No Microphone Found</h2>
    <p class="mic-permission-modal__description">
      We couldn't detect a microphone on your device. Please make sure:
    </p>
    <ul style="list-style: none; padding: 0; margin: 0 0 24px;">
      <li style="padding: 10px 0 10px 32px; position: relative; color: #111827; border-bottom: 1px solid #e5e7eb;">
        <span style="position: absolute; left: 0; font-size: 18px;">☐</span>
        Your microphone is properly connected
      </li>
      <li style="padding: 10px 0 10px 32px; position: relative; color: #111827; border-bottom: 1px solid #e5e7eb;">
        <span style="position: absolute; left: 0; font-size: 18px;">☐</span>
        Your microphone is not muted at the hardware level
      </li>
      <li style="padding: 10px 0 10px 32px; position: relative; color: #111827;">
        <span style="position: absolute; left: 0; font-size: 18px;">☐</span>
        No other app is exclusively using the microphone
      </li>
    </ul>
    <div class="mic-permission-modal__actions">
      <button class="mic-permission-modal__btn mic-permission-modal__btn--primary" data-action="retry">
        Retry
      </button>
      <button class="mic-permission-modal__btn mic-permission-modal__btn--secondary" data-action="close">
        Close
      </button>
    </div>
  `;
  
  overlay.appendChild(modalDiv);
  modal.overlay = overlay;
  modal.modal = modalDiv;
  
  // Event handlers
  modalDiv.querySelector('[data-action="retry"]').addEventListener('click', () => {
    window.location.reload();
  });
  
  modalDiv.querySelector('[data-action="close"]').addEventListener('click', () => {
    modal.close();
    onClose();
  });
  
  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      modal.close();
      onClose();
    }
  });
  
  modal.show();
  return modal;
}

/**
 * Server-driven disclaimer (legal copy from backend hello_ack). Plain text only.
 */
export function showServerDisclaimerModal({ texts, onAccept, onCancel }) {
  const modal = new MicPermissionModal();
  const overlay = modal.createOverlay();
  overlay.style.background = 'rgba(0, 0, 0, 0.5)';
  overlay.style.backdropFilter = 'blur(2px)';

  const isMobile = isMobileDevice();

  const modalDiv = document.createElement('div');
  modalDiv.className = 'mic-permission-modal' + (isMobile ? ' mic-permission-modal--mobile' : '');

  const lines = (texts || []).map((t) => String(t).trim()).filter(Boolean);
  const body = lines.length ? lines.join('\n\n') : 'Please confirm to continue.';
  const safe = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  modalDiv.innerHTML = `
    <h2 class="mic-permission-modal__title">Notice</h2>
    <div class="mic-permission-modal__description" style="white-space:pre-wrap;max-height:50vh;overflow:auto;text-align:left;font-size:15px;line-height:1.5;">${safe}</div>
    <div class="mic-permission-modal__actions">
      <button type="button" class="mic-permission-modal__btn mic-permission-modal__btn--primary" data-action="accept">Accept</button>
      <button type="button" class="mic-permission-modal__btn mic-permission-modal__btn--secondary" data-action="cancel">Decline</button>
    </div>
  `;

  if (isMobile) {
    overlay.style.alignItems = 'flex-end';
    overlay.style.paddingBottom = 'max(20px, env(safe-area-inset-bottom, 20px))';
    overlay.style.paddingTop = '20px';
  }

  overlay.appendChild(modalDiv);
  modal.overlay = overlay;
  modal.modal = modalDiv;

  modalDiv.querySelector('[data-action="accept"]').addEventListener('click', () => {
    modal.close();
    onAccept();
  });

  modalDiv.querySelector('[data-action="cancel"]').addEventListener('click', () => {
    modal.close();
    onCancel();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      modal.close();
      onCancel();
    }
  });

  modal.show();
  return modal;
}

/**
 * Disclaimer modal shown after hello_ack (before greeting/TTS starts).
 * Compact card design with point rows, RTL support for Hebrew/Arabic.
 *
 * @param {Object} options
 * @param {string[]} options.texts - Disclaimer texts from server hello_ack
 * @param {string} [options.direction] - 'rtl' or 'ltr' (default: 'ltr')
 * @param {Function} options.onAccept - Called when user accepts
 * @param {Function} options.onCancel - Called when user declines
 */
export function showDisclaimerModal({ texts, direction, onAccept, onCancel }) {
  const modal = new MicPermissionModal();
  const overlay = modal.createOverlay();
  const isMobile = isMobileDevice();
  const isRtl = direction === 'rtl';
  const dir = isRtl ? 'rtl' : 'ltr';

  // Drop .mic-permission-overlay so injected stylesheet (0.6 scrim + heavy blur) cannot stack
  // with our inline scrim. Animated overlay opacity also composites badly with backdrop-filter
  // in WebKit/Chrome — looks "lighter" until the fade ends or after the first tap reflows layers.
  overlay.removeAttribute('class');

  // Overlay: stable opacity (no fade on the scrim). Animate the card only (mic-slideUp below).
  overlay.style.cssText = `
    position:fixed;inset:0;
    background:rgba(0,0,0,0.5);
    display:flex;align-items:center;justify-content:center;
    z-index:100001;
    opacity:1;
    isolation:isolate;
  `;
  overlay.setAttribute('dir', dir);

  if (isMobile) {
    overlay.style.alignItems = 'flex-end';
    overlay.style.padding = '12px';
    overlay.style.paddingBottom = 'max(12px, env(safe-area-inset-bottom, 12px))';
  }

  // Modal card
  const modalDiv = document.createElement('div');
  modalDiv.setAttribute('role', 'dialog');
  modalDiv.setAttribute('aria-modal', 'true');
  modalDiv.setAttribute('dir', dir);
  modalDiv.style.cssText = `
    background:#fff;
    border-radius:16px;
    border:0.5px solid rgba(0,0,0,0.12);
    width:${isMobile ? '100%' : '320px'};
    max-width:360px;
    overflow:hidden;
    animation:mic-slideUpSolid 0.22s ease forwards;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
    direction:${dir};
    opacity:1;
  `;

  // --- Localized labels ---
  const titleLabel = isRtl ? '\u05E9\u05D9\u05D7\u05EA \u05E7\u05D5\u05DC' : 'Start voice call';
  const subtitleLabel = isRtl ? '\u05D0\u05E0\u05D0 \u05E7\u05E8\u05D0 \u05DC\u05E4\u05E0\u05D9 \u05E9\u05DE\u05DE\u05E9\u05D9\u05DB\u05D9\u05DD' : 'Please review before continuing';
  const statusLabel = isRtl ? '\u05D4\u05E0\u05E6\u05D9\u05D2 \u05DE\u05D5\u05DB\u05DF \u05D5\u05D6\u05DE\u05D9\u05DF' : 'Agent is online and ready';
  const acceptLabel = isRtl ? '\u05D0\u05D9\u05E9\u05D5\u05E8 \u05D5\u05D4\u05EA\u05D7\u05DC\u05EA \u05E9\u05D9\u05D7\u05D4' : 'Accept & start call';
  const declineLabel = isRtl ? '\u05DC\u05D0 \u05EA\u05D5\u05D3\u05D4' : 'No thanks';

  // --- Point icons (SVG) ---
  const pointIcons = [
    // Record / circle icon
    `<svg viewBox="0 0 14 14" fill="none" style="width:14px;height:14px;">
      <circle cx="7" cy="7" r="2.5" fill="#888"/>
      <circle cx="7" cy="7" r="5" stroke="#888" stroke-width="1"/>
    </svg>`,
    // AI / robot icon
    `<svg viewBox="0 0 14 14" fill="none" style="width:14px;height:14px;">
      <rect x="3" y="3" width="8" height="5" rx="1.5" fill="#888"/>
      <path d="M5 8v2.5M9 8v2.5" stroke="#888" stroke-width="1.2" stroke-linecap="round"/>
      <path d="M5 5.5h4" stroke="white" stroke-width="1" stroke-linecap="round"/>
    </svg>`,
    // Lock / privacy icon
    `<svg viewBox="0 0 14 14" fill="none" style="width:14px;height:14px;">
      <rect x="3" y="6" width="8" height="6" rx="1.5" fill="#888"/>
      <path d="M5 6V4.5a2 2 0 014 0V6" stroke="#888" stroke-width="1.2" stroke-linecap="round"/>
    </svg>`
  ];

  // --- Build points HTML ---
  const lines = (texts || []).map((t) => String(t).trim()).filter(Boolean);
  const pointsHtml = lines.slice(0, 3).map((text, i) => {
    const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const icon = pointIcons[i % pointIcons.length];
    return `
      <div style="display:flex;align-items:flex-start;gap:11px;padding:11px 12px;border-radius:10px;background:#f7f7f8;">
        <div style="width:30px;height:30px;border-radius:8px;background:#fff;border:0.5px solid rgba(0,0,0,0.1);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          ${icon}
        </div>
        <div style="font-size:12px;color:#555;line-height:1.5;padding-top:5px;">${safe}</div>
      </div>
    `;
  }).join('');

  // --- Assemble modal ---
  modalDiv.innerHTML = `
    <div style="padding:20px 22px 16px;border-bottom:0.5px solid rgba(0,0,0,0.08);">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px;">
        <div style="width:40px;height:40px;border-radius:12px;background:#6366f1;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg viewBox="0 0 20 20" fill="none" style="width:20px;height:20px;">
            <rect x="6.5" y="2" width="7" height="9" rx="3.5" fill="white"/>
            <path d="M4 10c0 3.314 2.686 6 6 6s6-2.686 6-6" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M10 16v2" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div>
          <p style="font-size:15px;font-weight:600;color:#111;margin:0 0 2px;">${titleLabel}</p>
          <p style="font-size:11px;color:#999;margin:0;">${subtitleLabel}</p>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:6px;">
        <div style="width:6px;height:6px;border-radius:50%;background:#4ade80;"></div>
        <span style="font-size:11px;color:#aaa;">${statusLabel}</span>
      </div>
    </div>

    <div style="padding:14px 22px;border-bottom:0.5px solid rgba(0,0,0,0.08);display:flex;flex-direction:column;gap:8px;">
      ${pointsHtml}
    </div>

    <div style="padding:14px 22px 18px;display:flex;flex-direction:column;gap:8px;">
      <button type="button" data-action="accept" style="
        width:100%;padding:11px;border-radius:10px;background:#6366f1;border:none;
        color:#fff;font-size:13px;font-weight:600;cursor:pointer;
        display:flex;align-items:center;justify-content:center;gap:7px;
        transition:background 0.15s;touch-action:manipulation;-webkit-tap-highlight-color:transparent;
      ">
        <svg viewBox="0 0 14 14" fill="none" style="width:14px;height:14px;">
          <rect x="4.5" y="1" width="5" height="8" rx="2.5" fill="white"/>
          <path d="M2 7c0 2.76 2.24 5 5 5s5-2.24 5-5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
          <path d="M7 12v1.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
        ${acceptLabel}
      </button>
      <button type="button" data-action="cancel" style="
        width:100%;padding:9px;border-radius:10px;background:transparent;
        border:0.5px solid rgba(0,0,0,0.12);color:#888;font-size:12px;
        cursor:pointer;transition:background 0.15s,color 0.15s;
        touch-action:manipulation;-webkit-tap-highlight-color:transparent;
      ">${declineLabel}</button>
    </div>
  `;

  overlay.appendChild(modalDiv);
  modal.overlay = overlay;
  modal.modal = modalDiv;

  // --- Event handlers ---
  // Touch: prefer pointerup (click is unreliable on some WebKit builds over layered UI).
  // pointerup preventDefault suppresses the follow-up synthetic click — keyboard still uses click.
  let actionConsumed = false;
  const scheduleClose = (fn) => {
    if (actionConsumed) return;
    actionConsumed = true;
    setTimeout(() => {
      modal.close();
      fn();
    }, 0);
  };

  const bindPrimary = (btn, fn) => {
    const onPointerUp = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      scheduleClose(fn);
    };
    const onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (actionConsumed) return;
      scheduleClose(fn);
    };
    btn.addEventListener('pointerup', onPointerUp, { capture: true, passive: false });
    btn.addEventListener('click', onClick, { capture: true });
  };

  const acceptBtn = modalDiv.querySelector('[data-action="accept"]');
  const declineBtn = modalDiv.querySelector('[data-action="cancel"]');
  bindPrimary(acceptBtn, onAccept);
  bindPrimary(declineBtn, onCancel);

  if (!isMobile) {
    acceptBtn.addEventListener('mouseenter', () => { acceptBtn.style.background = '#4f46e5'; });
    acceptBtn.addEventListener('mouseleave', () => { acceptBtn.style.background = '#6366f1'; });
    declineBtn.addEventListener('mouseenter', () => { declineBtn.style.background = '#f5f5f5'; declineBtn.style.color = '#555'; });
    declineBtn.addEventListener('mouseleave', () => { declineBtn.style.background = 'transparent'; declineBtn.style.color = '#888'; });
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      e.stopPropagation();
      scheduleClose(onCancel);
    }
  });

  modal.show();
  return modal;
}
